import { useState, useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { Trash2Icon, EditIcon } from 'lucide-react';
import { useDetachUsersMutation } from '../hooks/useDetachUsersMutation';
import { AttachUsersModal } from './AttachUsersModal';
import { GroupPermissionBadges } from './GroupPermissionBadges';
import { UserPermissionsEditor } from './UserPermissionsEditor';
import type { UserPermissionEntry, UserDealerPermissionEntry, DealerRef } from '../types/security-admin.types';

interface Props {
  groupId: string;
  userPermissions: UserPermissionEntry[];
  userDealerPermissions: UserDealerPermissionEntry[];
  groupDealers: DealerRef[];
}

export function SecurityGroupUsersPanel({ groupId, userPermissions, userDealerPermissions, groupDealers }: Props) {
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const detachMutation = useDetachUsersMutation(groupId);

  const editingUserPermissions = editingUserId
    ? userPermissions.find((up) => up.user_id === editingUserId)
    : undefined;
  const editingUserDealerPerms = editingUserId
    ? userDealerPermissions.filter((udp) => udp.user_id === editingUserId)
    : [];

  const columns = useMemo<ColumnDef<UserPermissionEntry>[]>(() => [
    {
      id: 'user',
      header: 'User',
      cell: (info) => {
        const { user_id, name } = info.row.original;
        return (
          <TextLink
            to={`/admin/users/${user_id}`}
            color="secondary" className="font-semibold"
          >
            {name ?? <span className="font-mono text-xs">{user_id}</span>}
          </TextLink>
        );
      },
    },
    {
      id: 'group_permissions',
      header: 'Group Permissions',
      cell: (info) => <GroupPermissionBadges permissions={info.row.original.permissions} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: (info) => {
        const userId = info.row.original.user_id;
        // Icon-only Buttons require explicit `ariaLabel` — `Tooltip` is a
        // visual-only hint and does not contribute an accessible name. We
        // suffix the user id so identical icons across rows are
        // distinguishable to assistive tech.
        return (
          <div className="flex space-x-2">
            <PermissionGuard permission={{ resource: 'security_group', action: 'manage_user_permissions' }}>
              <Tooltip content="Edit permissions">
                <TextLink
                  onClick={() => setEditingUserId(userId)}
                  color="secondary"
                  startIcon={<EditIcon size={18} />}
                  ariaLabel={`Edit permissions for user ${userId}`}
                />
              </Tooltip>
            </PermissionGuard>
            <PermissionGuard permission={{ resource: 'security_group', action: 'manage_users' }}>
              <Tooltip content="Detach user">
                <TextLink
                  onClick={() => detachMutation.mutate([userId])}
                  color="error"
                  startIcon={<Trash2Icon size={18} />}
                  disabled={detachMutation.isPending}
                  ariaLabel={`Detach user ${userId}`}
                />
              </Tooltip>
            </PermissionGuard>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [detachMutation.isPending]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Users</h3>
        <PermissionGuard permission={{ resource: 'security_group', action: 'manage_users' }}>
          <Button size="sm" type="button" onClick={() => setShowAttachModal(true)}>
            Attach Users
          </Button>
        </PermissionGuard>
      </div>

      {userPermissions.length === 0 ? (
        <p className="text-mp-muted text-sm">No users attached to this security group.</p>
      ) : (
        <Table<UserPermissionEntry> data={userPermissions} columns={columns} enableSorting={false} />
      )}

      {showAttachModal && (
        <AttachUsersModal
          groupId={groupId}
          attachedUserIds={userPermissions.map((up) => up.user_id)}
          onClose={() => setShowAttachModal(false)}
        />
      )}

      {editingUserId && (
        <UserPermissionsEditor
          groupId={groupId}
          userId={editingUserId}
          existingPermissions={editingUserPermissions}
          existingDealerPermissions={editingUserDealerPerms}
          groupDealers={groupDealers}
          onClose={() => setEditingUserId(null)}
        />
      )}
    </div>
  );
}
